import type { Card } from "@amzn/ads-blink-client";
import { APPENV } from "@amzn/midway-cognito-type-script-provider";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useStore } from "zustand";

import { authStore } from "../../store/auth";

export type CardWithRecommendation = Card & {
    recommendation?: string;
};

const transformInsightToCard = (insight: any): CardWithRecommendation => {
    return {
        identifier: insight.identifier,
        header: insight.card_header,
        cardHeader: insight.card_header,
        title: insight.card_header,
        insightType: insight.insight_type,
        description: insight.description,
        lastModifiedDate: insight.last_modified_date,
        additionalInfo: insight.additional_info,
        details: insight.details,
        recommendation: insight.recommendation,
    } as CardWithRecommendation;
};

export function useGetAiRecommendedInsightsAsync(
    onCardReceived?: (card: CardWithRecommendation) => void,
    onRecommendationChunk?: (identifier: string, chunk: string) => void,
): [
    AsyncState<CardWithRecommendation[]>,
    UseAsyncActions<CardWithRecommendation[], []>,
] {
    const apiEndpoint = useStore(
        authStore,
        (state) => state.settings.apiEndpoint,
    );

    const [state, action] = useAsync(
        async (): Promise<CardWithRecommendation[]> => {
            const protocol = APPENV.mock_server ? "http" : "https";
            const apiUrl = `${protocol}://${apiEndpoint}/ai/recommendInsights`;

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({}),
            });

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch AI recommendations: ${response.status} ${response.statusText}`,
                );
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error("Response body is not readable");
            }

            const decoder = new TextDecoder();
            const extractedInsights: CardWithRecommendation[] = [];
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.event_type === "insight_extracted") {
                                const card = transformInsightToCard(
                                    data.insight,
                                );
                                extractedInsights.push(card);

                                if (onCardReceived) {
                                    onCardReceived(card);
                                }
                            } else if (data.event_type === "text_chunk") {
                                if (onRecommendationChunk) {
                                    onRecommendationChunk(
                                        data.identifier,
                                        data.content,
                                    );
                                }
                            }
                        } catch (error) {
                            console.error("Error parsing SSE event:", error);
                        }
                    }
                }
            }

            return extractedInsights;
        },
    );

    return [state as AsyncState<CardWithRecommendation[]>, action];
}
